import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, Check, MessageCircle, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { SERVICES, getServiceBySlug } from "@/constants/services";
import { IONA, waLink } from "@/constants/site";
import { SERVICE_SEO } from "@/constants/seo";
import useSEO from "@/hooks/useSEO";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);
  const seo = SERVICE_SEO[slug] || {};
  useSEO({
    title:
      seo.title ||
      (service
        ? `${service.title} · İONA Ümraniye Güzellik Merkezi`
        : "İONA Güzellik Merkezi"),
    description:
      seo.description ||
      (service ? service.short : "İONA Güzellik Merkezi · Ümraniye"),
    canonical: service ? `${IONA.siteUrl}/hizmetler/${slug}` : IONA.siteUrl,
    image: service ? `${IONA.siteUrl}${service.image}` : undefined,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!service) {
    return (
      <div data-testid="service-not-found" className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-32 pb-20 text-center iona-container">
          <h1 className="font-serif text-3xl text-[#1A1A1A] mb-4">
            Hizmet bulunamadı
          </h1>
          <Link to="/#hizmetler" className="text-[#5E2A84] hover:underline">
            Tüm hizmetlere dön
          </Link>
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    );
  }

  const Icon = Icons[service.icon] || Icons.Sparkles;

  // Suggest 3 other services (excluding current)
  const related = SERVICES.filter((s) => s.slug !== service.slug)
    .filter((s) => s.category === service.category)
    .slice(0, 3);
  const fallback = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);
  const suggestions = related.length >= 3 ? related : fallback;

  const waMessage = `Merhaba, ${service.title} hizmeti hakkında bilgi almak ve randevu oluşturmak istiyorum.`;

  return (
    <div data-testid="service-detail-page" className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-28 pb-20">
        {/* Hero */}
        <section className="iona-container">
          <button
            onClick={() => navigate(-1)}
            data-testid="service-back-btn"
            className="inline-flex items-center gap-2 text-[#5E2A84] hover:gap-3 transition-all text-sm font-medium mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Geri
          </button>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F4F1F9] text-[#5E2A84] text-xs uppercase tracking-[0.25em] mb-6">
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {service.category}
              </div>
              <h1
                data-testid="service-title"
                className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] leading-[1.05] tracking-tight mb-6"
              >
                {service.title}
              </h1>
              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-8">
                {service.short}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={waLink(waMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-whatsapp-cta"
                >
                  <Button className="w-full sm:w-auto h-14 px-7 rounded-full bg-[#5E2A84] hover:bg-[#4a2168] text-white iona-soft-shadow">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {service.cta}
                  </Button>
                </a>
                <a href={`tel:${IONA.phoneIntl}`} data-testid="service-call-btn">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto h-14 px-7 rounded-full border-[#5E2A84]/25 text-[#5E2A84] hover:bg-[#F4F1F9] hover:text-[#5E2A84]"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Hemen Ara
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-[2rem] overflow-hidden iona-soft-shadow aspect-[4/5]">
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#5E2A84]/20 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* Detailed description */}
        <section className="iona-container mt-20 lg:mt-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[#5E2A84] mb-4">
                — Uygulama Detayı
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] leading-[1.1] tracking-tight mb-6">
                Süreç ve uygulama hakkında
              </h2>
              <p
                data-testid="service-detail-text"
                className="text-base text-[#1A1A1A] leading-[1.85]"
              >
                {service.detail}
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-[#F4F1F9] p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-[#5E2A84] mb-5">
                  — Faydaları
                </div>
                <ul className="space-y-4">
                  {service.benefits.map((b, i) => (
                    <li
                      key={i}
                      data-testid={`service-benefit-${i}`}
                      className="flex items-start gap-3"
                    >
                      <span className="mt-1 w-5 h-5 rounded-full bg-[#5E2A84] text-white flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span className="text-[#1A1A1A] leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="iona-container mt-20">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#5E2A84] px-8 py-12 md:px-14 md:py-16">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-[#7A3FA8] blur-3xl opacity-40 translate-x-1/3 -translate-y-1/3 pointer-events-none" />
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-tight mb-3">
                  Ücretsiz danışmanlık için <span className="italic text-[#E8E2F0]">WhatsApp'tan</span> yazın
                </h3>
                <p className="text-white/80 max-w-xl">
                  Size en uygun planı oluşturmak için uzman ekibimizle iletişime
                  geçin. Ön görüşme ve deneme seansı ücretsizdir.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <a
                  href={waLink(waMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-bottom-cta"
                >
                  <Button className="h-13 px-7 py-3 rounded-full bg-white text-[#5E2A84] hover:bg-[#F4F1F9] iona-soft-shadow">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {service.cta}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Related services */}
        {suggestions.length > 0 && (
          <section className="iona-container mt-20">
            <div className="flex justify-between items-end mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#5E2A84] mb-3">
                  — Diğer Hizmetler
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">
                  İlginizi çekebilecek uygulamalar
                </h3>
              </div>
              <Link
                to="/#hizmetler"
                className="hidden sm:inline-flex items-center gap-2 text-[#5E2A84] text-sm font-medium hover:gap-3 transition-all"
              >
                Tümünü Gör
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {suggestions.map((s) => {
                const I = Icons[s.icon] || Icons.Sparkles;
                return (
                  <Link
                    key={s.slug}
                    to={`/hizmetler/${s.slug}`}
                    data-testid={`related-service-${s.slug}`}
                    className="group rounded-2xl overflow-hidden bg-[#F8F9FA] border border-[#F0F0F0] iona-hover-lift block"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3 text-[#5E2A84]">
                        <I className="w-4 h-4" strokeWidth={1.5} />
                        <span className="text-[10px] uppercase tracking-wider">
                          {s.category}
                        </span>
                      </div>
                      <h4 className="font-serif text-base text-[#1A1A1A] mb-2 group-hover:text-[#5E2A84] transition-colors leading-snug">
                        {s.title}
                      </h4>
                      <p className="text-xs text-[#4A4A4A] line-clamp-2">{s.short}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
